import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, shareReplay, timer } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { RequestDebounceConfigService } from "../services/utils/request-debounce-config.service";

interface CachedRequest {
  observable: Observable<HttpEvent<unknown>>;
  timestamp: number;
}

@Injectable()
export class RequestDebounceInterceptor implements HttpInterceptor {
  private readonly config = inject(RequestDebounceConfigService);

  private readonly cache = new Map<string, CachedRequest>();

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.shouldSkipDebounce(request)) {
      return next.handle(request);
    }

    const requestKey = this.generateRequestKey(request);
    const now = Date.now();
    const cachedRequest = this.cache.get(requestKey);

    if (
      cachedRequest &&
      now - cachedRequest.timestamp < this.config.getDebounceTime()
    ) {
      return cachedRequest.observable;
    }

    const requestObservable = next.handle(request).pipe(
      shareReplay(1),
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          this.scheduleCleanup(requestKey);
        }
      }),
      finalize(() => {
        this.scheduleCleanup(requestKey);
      })
    );

    this.cache.set(requestKey, {
      observable: requestObservable,
      timestamp: now,
    });

    return requestObservable;
  }

  private shouldSkipDebounce(request: HttpRequest<unknown>): boolean {
    const skipMethods = this.config.getSkipMethods();
    const methodShouldSkip = skipMethods.includes(request.method.toUpperCase());
    const urlShouldSkip = this.config.shouldSkipUrl(request.url);

    return methodShouldSkip || urlShouldSkip;
  }

  private generateRequestKey(request: HttpRequest<unknown>): string {
    const url = request.urlWithParams;
    const method = request.method;
    const body = request.body ? JSON.stringify(request.body) : "";

    return `${method}:${url}:${body}`;
  }

  private scheduleCleanup(requestKey: string): void {
    timer(this.config.getDebounceTime()).subscribe(() => {
      this.cache.delete(requestKey);
    });
  }
}
